import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Phone, User, ArrowRight, ArrowLeft, Loader2, Copy, CheckCircle, Hash, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AuthError } from '../components/AuthError';
import { authErrorMessage } from '../utils/authForm';

const CompanySignupPage = () => {
  const navigate = useNavigate();
  const { signupCompany, activateSession } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [companyCode, setCompanyCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({ companyName: '', ownerName: '', phone: '', ownerDigits4: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  // Live preview: 2 letters from company name + 4 digits
  const prefix = (formData.companyName || '').replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X');
  const previewCode = formData.ownerDigits4.length > 0 ? `${prefix}${formData.ownerDigits4.padEnd(4, '_')}` : '';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage('');
    if (!formData.companyName.trim() || !formData.ownerName.trim() || !formData.phone.trim()) {
      setErrorMessage('Please fill all fields'); return;
    }
    if (!formData.ownerDigits4 || formData.ownerDigits4.length !== 4 || !/^\d{4}$/.test(formData.ownerDigits4)) {
      setErrorMessage('Please choose exactly 4 digits (0000-9999)'); return;
    }
    setLoading(true);
    try {
      const result = await signupCompany(formData.companyName, formData.phone, formData.ownerName, formData.ownerDigits4);
      setRegistration(result);
      setCompanyCode(result.company_code);
      setSuccess(true);
      toast.success('Company created successfully!');
    } catch (error) {
      setErrorMessage(authErrorMessage(error, 'Signup failed'));
    } finally { setLoading(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(companyCode).then(() => {
      setCopied(true);
      toast.success('Code copied!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Copy unavailable. Please save the displayed company code.'));
  };
  const enterCompany = () => { activateSession(registration); navigate('/dashboard'); };

  // ══════════════════════════════════════════════════════════════════════════
  // SUCCESS STATE (Both Mobile & Desktop)
  // ══════════════════════════════════════════════════════════════════════════
  if (success) {
    return (
      <div data-testid="signup-success" style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '24px 20px',
        fontFamily: "'Manrope', 'Inter', sans-serif",
        background: 'linear-gradient(165deg, #f0fdfa 0%, #ecfdf5 30%, #dcfce7 60%, #f0fdf4 100%)',
        position: 'relative', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34,197,94,0.2) 0%, transparent 70%)', top: -100, right: 0 }} />
        </div>
        <div style={{
          width: 88, height: 88, borderRadius: 24, marginBottom: 24,
          background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 16px 48px rgba(34,197,94,0.4)', position: 'relative', zIndex: 1,
        }}>
          <CheckCircle style={{ width: 44, height: 44, color: '#fff' }} />
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 900, textAlign: 'center', margin: '0 0 8px', color: '#0f172a', position: 'relative', zIndex: 1 }}>
          Empire Created! 🏗️
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', margin: '0 0 28px', position: 'relative', zIndex: 1 }}>
          Save your company code
        </p>
        <div style={{
          width: '100%', maxWidth: 320, background: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '24px', textAlign: 'center',
          border: '1px solid rgba(34,197,94,0.2)', boxShadow: '0 8px 32px rgba(34,197,94,0.15)',
          backdropFilter: 'blur(12px)', marginBottom: 20, position: 'relative', zIndex: 1,
        }}>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#22c55e', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 12 }}>Your Company Code</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
            <span data-testid="created-company-code" style={{ fontSize: 32, fontWeight: 900, letterSpacing: 0,
              background: 'linear-gradient(135deg, #22c55e, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {companyCode}
            </span>
            <button data-testid="company-signup-copy-code" aria-label="Copy company code" title="Copy company code" onClick={copyCode} style={{
              width: 44, height: 44, flexShrink: 0, borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg, #22c55e, #10b981)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(34,197,94,0.3)',
            }}>
              {copied ? <CheckCircle style={{ width: 20, height: 20, color: '#fff' }} /> : <Copy style={{ width: 20, height: 20, color: '#fff' }} />}
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>Share this code with your team</p>
        </div>
        <button data-testid="company-signup-continue" onClick={enterCompany} style={{
          width: '100%', maxWidth: 320, height: 54, borderRadius: 14, border: 'none',
          background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
          color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: '0 8px 24px rgba(13,148,136,0.35)', position: 'relative', zIndex: 1,
        }}>
          Enter Your Empire <ArrowRight style={{ width: 18, height: 18 }} />
        </button>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div data-testid="company-signup-page" style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '24px 20px',
        fontFamily: "'Manrope', 'Inter', sans-serif",
        background: 'linear-gradient(165deg, #fffbeb 0%, #fef3c7 30%, #fde68a 60%, #fef9c3 100%)',
        position: 'relative', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(251,191,36,0.2) 0%, transparent 70%)',
            top: -100, right: 0, animation: 'float 8s ease-in-out infinite' }} />
        </div>
        <Link data-testid="company-signup-back-link" to="/login" style={{
          position: 'absolute', top: 20, left: 20, width: 40, height: 40, borderRadius: 12,
          background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(251,191,36,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', zIndex: 10,
        }}>
          <ArrowLeft style={{ width: 20, height: 20, color: '#92400e' }} />
        </Link>
        <div style={{ textAlign: 'center', marginBottom: 28, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏗️</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: '#78350f', margin: '0 0 6px' }}>Create Company</h1>
          <p style={{ fontSize: 12, color: '#92400e', margin: 0, opacity: 0.8 }}>Start your construction empire</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="company-signup-form" style={{
          width: '100%', maxWidth: 340, background: 'rgba(255,255,255,0.9)', borderRadius: 24, padding: '28px 24px',
          border: '1px solid rgba(251,191,36,0.3)', boxShadow: '0 8px 32px rgba(251,191,36,0.15)',
          backdropFilter: 'blur(20px)', position: 'relative', zIndex: 1,
        }}>
          {[{ key: 'companyName', label: 'Company Name', placeholder: 'e.g. Sharma Constructions', icon: Building2 },
            { key: 'ownerName', label: 'Owner Name', placeholder: 'Your full name', icon: User },
            { key: 'phone', label: 'Phone Number', placeholder: 'Your phone number', icon: Phone, type: 'tel' },
          ].map(({ key, label, placeholder, icon: Icon, type }) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 6 }}>{label}</label>
              <div style={{ position: 'relative' }}>
                <Icon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                  width: 18, height: 18, color: focusedField === key ? '#f59e0b' : '#d4a574', transition: 'color 0.2s' }} />
                <Input type={type || 'text'} placeholder={placeholder} value={formData[key]} data-testid={`company-signup-${key.replace(/[A-Z]/g, c => '-' + c.toLowerCase())}`}
                  onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                  onFocus={() => setFocusedField(key)} onBlur={() => setFocusedField(null)}
                  style={{
                    width: '100%', height: 52, paddingLeft: 46, fontSize: 15, fontWeight: 500,
                    background: focusedField === key ? '#fff' : '#fffbeb',
                    border: focusedField === key ? '2px solid #f59e0b' : '2px solid #fde68a',
                    borderRadius: 14, color: '#78350f', outline: 'none', transition: 'all 0.2s',
                  }} />
              </div>
            </div>
          ))}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 6 }}>Choose 4 Digits</label>
            <div style={{ position: 'relative' }}>
              <Hash style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'code' ? '#f59e0b' : '#d4a574', transition: 'color 0.2s' }} />
              <Input type="text" placeholder="e.g. 5524" value={formData.ownerDigits4} data-testid="company-signup-digits" inputMode="numeric"
                onChange={e => setFormData({ ...formData, ownerDigits4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                onFocus={() => setFocusedField('code')} onBlur={() => setFocusedField(null)} maxLength={4}
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 22, fontWeight: 800, letterSpacing: '0.3em', textAlign: 'center',
                  background: focusedField === 'code' ? '#fff' : '#fffbeb',
                  border: focusedField === 'code' ? '2px solid #f59e0b' : '2px solid #fde68a',
                  borderRadius: 14, color: '#78350f', outline: 'none', transition: 'all 0.2s',
                }} />
            </div>
            {previewCode && (
              <div data-testid="code-preview" style={{ textAlign: 'center', marginTop: 10, padding: '8px 16px', borderRadius: 10,
                background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', border: '1px solid #fde68a' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: '0.1em' }}>YOUR CODE: </span>
                <span style={{ fontSize: 20, fontWeight: 900, letterSpacing: '0.2em', color: '#d97706' }}>{previewCode}</span>
              </div>
            )}
          </div>
          <AuthError message={errorMessage} testId="company-signup-error" />
          <button type="submit" disabled={loading} data-testid="company-signup-submit"
            style={{
              width: '100%', height: 54, borderRadius: 14, border: 'none',
              background: loading ? 'rgba(251,191,36,0.5)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#fff', fontSize: 15, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: loading ? 'none' : '0 8px 24px rgba(245,158,11,0.4)', transition: 'all 0.2s',
            }}>
            {loading ? <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} />
              : <>Create Empire <Sparkles style={{ width: 18, height: 18 }} /></>}
          </button>
        </form>
        <p style={{ fontSize: 12, color: '#92400e', marginTop: 20, textAlign: 'center', position: 'relative', zIndex: 1, opacity: 0.8 }}>
          Already have a company? <Link data-testid="company-signup-login-link" to="/login" style={{ color: '#d97706', fontWeight: 700, textDecoration: 'none' }}>Sign In</Link>
        </p>
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
          input::placeholder { color: #d4a574 !important; }
        `}</style>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT (2-Column)
  // ══════════════════════════════════════════════════════════════════════════
  const BG = 'linear-gradient(150deg,#f0fdfa 0%,#fdf4ff 35%,#fff7ed 65%,#f0fdf9 100%)';
  const fields = [
    { id: 'companyName', icon: Building2, label: 'Company Name', placeholder: "e.g. KKT Constructions", type: 'text', key: 'companyName', color: '#0d9488' },
    { id: 'ownerName', icon: User, label: 'Owner Name', placeholder: "Your full name", type: 'text', key: 'ownerName', color: '#7c3aed' },
    { id: 'phone', icon: Phone, label: 'Phone Number', placeholder: "Your phone number", type: 'tel', key: 'phone', color: '#ec4899' },
    { id: 'code', icon: Hash, label: 'Choose 4 Digits', placeholder: "e.g. 5524", type: 'text', key: 'ownerDigits4', color: '#f97316', maxLen: 4, isCode: true },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Manrope','Inter',sans-serif", background: BG, position: 'relative', overflowX: 'hidden', overflowY: 'auto' }}>
      {/* Jali */}
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0, opacity: 0.04 }}>
        <defs>
          <pattern id="jaliS" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <circle cx="30" cy="30" r="12" fill="none" stroke="#0d9488" strokeWidth="0.7"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#jaliS)"/>
      </svg>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(13,148,136,0.09) 0%,transparent 65%)', top: -200, left: -150 }}/>
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(124,58,237,0.07) 0%,transparent 65%)', top: -80, right: 0 }}/>
      </div>

      {/* Left — Brand */}
      <div style={{ flex: '0 0 44%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '56px 52px', position: 'relative', zIndex: 1 }}>
        <Link data-testid="company-signup-brand-link" to="/login" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899)', padding: 3, boxShadow: '0 6px 20px rgba(13,148,136,0.3)' }}>
            <img src="/sitepe-logo.png" alt="SitePe" style={{ width: '100%', height: '100%', borderRadius: 9, display: 'block' }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: '-0.02em' }}>
              <span style={{ color: '#0f172a' }}>site</span>
              <span style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>pe</span>
            </div>
            <div style={{ fontSize: 8, color: 'rgba(13,148,136,0.6)', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Construction ERP</div>
          </div>
        </Link>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 12px', borderRadius: 20,
          background: 'linear-gradient(135deg,rgba(13,148,136,0.08),rgba(124,58,237,0.06))',
          border: '1px solid rgba(13,148,136,0.2)', marginBottom: 16, width: 'fit-content' }}>
          <span style={{ fontSize: 11 }}>👑</span>
          <span style={{ fontSize: 9, fontWeight: 800, background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.1em' }}>START YOUR EMPIRE FREE</span>
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, color: '#0f172a', margin: '0 0 14px', lineHeight: 1.1, letterSpacing: '-0.03em' }}>
          Create Your<br/>
          <span style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899,#f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Construction Empire</span>
        </h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 32px', lineHeight: 1.7, maxWidth: 340 }}>
          Set up your company in under 2 minutes. Manage sites, bills, inventory and your entire team from one dashboard.
        </p>
        {[['1', 'Fill company details', '30 seconds'], ['2', 'Choose your 4-digit code', 'Your team uses this to join'], ['3', 'Start managing', '100+ features ready instantly']].map(([n, t, s], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0,
              background: `linear-gradient(135deg,${['#0d9488', '#7c3aed', '#ec4899'][i]},${['#14b8a6', '#a855f7', '#f472b6'][i]})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff' }}>{n}</div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>{t}</p>
              <p style={{ fontSize: 10, color: '#94a3b8', margin: 0 }}>{s}</p>
            </div>
          </div>
        ))}
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 24 }}>
          Already registered? <Link data-testid="company-signup-login-link" to="/login" style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 700, textDecoration: 'none' }}>Sign In →</Link>
        </p>
      </div>

      {/* Right — Form */}
      <div style={{ flex: '0 0 56%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 56px', position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'absolute', left: 0, top: '10%', height: '80%', width: 1,
          background: 'linear-gradient(to bottom,transparent,rgba(13,148,136,0.2),rgba(124,58,237,0.15),transparent)' }}/>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to right,transparent,rgba(13,148,136,0.35))' }}/>
              <span style={{ fontSize: 13, background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>✦</span>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to left,transparent,rgba(124,58,237,0.3))' }}/>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', margin: '0 0 5px', letterSpacing: '-0.02em' }}>Launch Your Company</h2>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Free forever for small teams</p>
          </div>
          <form onSubmit={handleSubmit} data-testid="company-signup-form" style={{ background: 'rgba(255,255,255,0.75)', borderRadius: 22, padding: '28px 24px',
            border: '1.5px solid rgba(13,148,136,0.12)', backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.03),0 20px 48px rgba(13,148,136,0.08)' }}>
            {fields.map(({ id, icon: Icon, label, placeholder, type, key, color, maxLen, isCode }) => (
              <div key={id} style={{ marginBottom: 16 }}>
                <Label style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', display: 'block', marginBottom: 7, color: `${color}99` }}>{label}</Label>
                <div style={{ position: 'relative' }}>
                  <Icon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, transition: 'color 0.2s', color: focusedField === id ? color : '#cbd5e1' }}/>
                  <Input type={type} placeholder={placeholder} value={formData[key]} data-testid={`company-signup-${key === 'ownerDigits4' ? 'digits' : key.replace(/[A-Z]/g, c => '-' + c.toLowerCase())}`}
                    onChange={e => setFormData({ ...formData, [key]: maxLen ? e.target.value.replace(/\D/g, '').slice(0, maxLen) : e.target.value })}
                    onFocus={() => setFocusedField(id)} onBlur={() => setFocusedField(null)} maxLength={maxLen}
                    style={{
                      width: '100%', height: 44, paddingLeft: 38, fontSize: isCode ? 20 : 14,
                      fontWeight: isCode ? 700 : 500, letterSpacing: isCode ? '0.3em' : 'normal',
                      background: '#fff', border: focusedField === id ? `1.5px solid ${color}` : '1.5px solid #e2e8f0',
                      borderRadius: 11, color: '#0f172a', outline: 'none',
                      boxShadow: focusedField === id ? `0 0 0 3px ${color}18` : 'none', transition: 'all 0.2s',
                    }}/>
                </div>
                {isCode && previewCode && (
                  <div data-testid="code-preview-desktop" style={{ textAlign: 'center', marginTop: 8, padding: '6px 12px', borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(249,115,22,0.08), rgba(249,115,22,0.04))', border: '1px solid rgba(249,115,22,0.2)' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#9a3412', letterSpacing: '0.1em' }}>YOUR CODE: </span>
                    <span style={{ fontSize: 18, fontWeight: 900, letterSpacing: '0.2em',
                      background: 'linear-gradient(135deg, #f97316, #ea580c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{previewCode}</span>
                  </div>
                )}
              </div>
            ))}
            <AuthError message={errorMessage} testId="company-signup-error" />
            <button type="submit" disabled={loading} data-testid="company-signup-submit"
              style={{
                width: '100%', height: 48, borderRadius: 13, border: 'none', marginTop: 8,
                background: loading ? 'rgba(13,148,136,0.5)' : 'linear-gradient(135deg,#0d9488 0%,#7c3aed 50%,#ec4899 100%)',
                color: '#fff', fontSize: 14, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(13,148,136,0.25)', transition: 'all 0.2s',
              }}>
              {loading ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <>Create Empire <Sparkles style={{ width: 14, height: 14 }} /></>}
            </button>
          </form>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default CompanySignupPage;
